import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
// Serialization
import { deserialize, serialize } from 'serializr';
import { paginatedServiceFeeRuleSchema, serviceFeeRuleSchema } from './schemas';
// Fragments
import { carparkForServiceFeeListFragment, serviceFeeRuleFragment } from './fragments';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';

/**
 * Serialization
 */
const deserializePaginatedServiceFeeRule = (data) => deserialize(paginatedServiceFeeRuleSchema, data);
const deserializeServiceFeeRule = (data) => deserialize(serviceFeeRuleSchema, data);
const serializeServiceFeeRule = (data) => serialize(serviceFeeRuleSchema, data);

/**
 * GraphQL
 */
const queGetServiceFeeRuleCarparks = `
  query GetServiceFeeRule(
    $input: GetServiceFeeRuleInput!,
    $carparksPagination: PaginationInput,
    $carparksSearch: ListCarparksSearchInput,
    $carparksSort: [ListCarparksSortInput!]
  ) {
    getServiceFeeRule(input: $input) {
      carparks(
        pagination: $carparksPagination,
        search: $carparksSearch,
        sort: $carparksSort
      ) {
        entries { ...carparkForServiceFeeListFragment }
        pageNumber
        pageSize
        totalEntries
        totalPages
      }
    }
  }

  ${carparkForServiceFeeListFragment}
`;

const queListServiceFeeRules = `
  query ListServiceFeeRules(
    $input: ListServiceFeeRulesInput!,
    $pagination: PaginationInput,
    $search: ListServiceFeeRulesSearchInput,
    $sort: [ListServiceFeeRulesSortInput!]
  ) {
    listServiceFeeRules(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...serviceFeeRuleFragment
      }
      pageNumber
      pageSize
      totalEntries
      totalPages
    }
  }

  ${serviceFeeRuleFragment}
`;

const mutCreateServiceFeeRule = `
  mutation CreateServiceFeeRule($input: CreateServiceFeeRuleInput!) {
    createServiceFeeRule(input: $input) {
      id
    }
  }
`;

const mutDeleteServiceFeeRule = `
  mutation DeleteServiceFeeRule($input: DeleteServiceFeeRuleInput!) {
    deleteServiceFeeRule(input: $input) {
      id
    }
  }
`;

const mutUpdateServiceFeeRule = `
  mutation UpdateServiceFeeRule($input: UpdateServiceFeeRuleInput!) {
    updateServiceFeeRule(input: $input) {
      id
    }
  }
`;

const listDefaultParams = {
  pagination: {
    page: 1,
    pageSize: 20,
  },
  sort: [{ field: 'name', dir: 'asc' }],
};

const nameColumn = {
  key: 'name',
  labelId: 'field.name',
  field: 'name',
  sortable: true,
};

const valueColumn = {
  key: 'value',
  labelId: 'field.value',
  field: 'value',
  sortable: false,
};

const priceThresholdColumn = {
  key: 'priceThreshold',
  labelId: 'field.priceThreshold',
  field: 'priceThreshold',
  sortable: false,
};

const minimalAmountColumn = {
  key: 'minimalAmount',
  labelId: 'field.minimalAmount',
  field: 'minimalAmount',
  sortable: false,
};

const maximalAmountColumn = {
  key: 'maximalAmount',
  labelId: 'field.maximalAmount',
  field: 'maximalAmount',
  sortable: false,
};

const carparksColumn = {
  key: 'carparks',
  labelId: 'field.carparks',
  field: 'carparks',
  sortable: false,
};

const nameSearchField = {
  key: 'name',
  labelId: 'field.name',
  field: 'name',
  initial: '',
  isUndefined: (value) => _isEmpty(value),
  needGetOnInit: false,
  type: SEARCHFIELD_TYPES.string,
  filter: {
    field: 'name',
  },
};

const carparkSearchField = {
  key: 'carpark',
  labelId: 'field.carpark',
  field: 'carpark',
  initial: null,
  isUndefined: (value) => _isNil(value),
  needGetOnInit: true,
  type: SEARCHFIELD_TYPES.resource,
  filter: {
    field: 'carparkId',
    key: 'id',
  },
};

/**
 * Model
 */
const serviceFeeRule = {
  name: 'serviceFeeRule',

  // Serialization
  deserialize: deserializeServiceFeeRule,
  deserializePaginated: deserializePaginatedServiceFeeRule,
  serialize: serializeServiceFeeRule,

  // List features
  listFeatures: {
    columns: [
      nameColumn,
      valueColumn,
      priceThresholdColumn,
      minimalAmountColumn,
      maximalAmountColumn,
      carparksColumn,
    ],
    columnsQuantity: () => ({
      xs: 6,
      sm: 6,
      md: 6,
      lg: 6,
      xl: 6,
    }),
    defaultParams: listDefaultParams,
    searchFields: [
      nameSearchField,
      carparkSearchField,
    ],
  },

  chooseListFeatures: {
    columns: [
      nameColumn,
      valueColumn,
      priceThresholdColumn,
      minimalAmountColumn,
      maximalAmountColumn,
    ],
    columnsQuantity: () => ({
      xs: 5,
      sm: 5,
      md: 5,
      lg: 5,
      xl: 5,
    }),
    defaultParams: listDefaultParams,
    searchFields: [nameSearchField],
  },

  // GraphQL
  queGetServiceFeeRuleCarparks,
  queListServiceFeeRules,
  mutCreateServiceFeeRule,
  mutDeleteServiceFeeRule,
  mutUpdateServiceFeeRule,
};

module.exports = serviceFeeRule;



// WEBPACK FOOTER //
// ./models/cpm/serviceFeeRule.js